import React from 'react';
import { Linkedin } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Public "verified candidate" share controls for a CHIRE credential.
 * - "Add to LinkedIn profile" → opens LinkedIn's certification add flow.
 * - "Share" → opens LinkedIn's share-a-post composer with the credential link.
 *
 * Props:
 *   url       (string) full public credential URL (required)
 *   title     (string) credential name shown as the certification name
 *   certId    (string) the credential token id (used as the LinkedIn cert id)
 *   issuedAt  (string|Date) issue date — sets the certification "issue" month/year
 *   compact   (bool) smaller pill buttons for inline lists
 */
export const LinkedInShareButtons = ({ url, title = 'CHIRE Verified Credential', certId = '', issuedAt, compact = false }) => {
  const issue = issuedAt ? new Date(issuedAt) : new Date();

  const addToProfileUrl =
    'https://www.linkedin.com/profile/add?' +
    new URLSearchParams({
      startTask: 'CERTIFICATION_NAME',
      name: title,
      organizationName: 'CHIRE',
      issueYear: String(issue.getFullYear()),
      issueMonth: String(issue.getMonth() + 1),
      certUrl: url,
      certId: certId || '',
    }).toString();

  const sharePostUrl =
    'https://www.linkedin.com/sharing/share-offsite/?' +
    new URLSearchParams({ url }).toString();

  const open = (target) => window.open(target, '_blank', 'noopener,noreferrer,width=720,height=640');

  const copyLink = () => {
    navigator.clipboard.writeText(url);
    toast.success('Verification link copied!');
  };

  const base = {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    borderRadius: 999, cursor: 'pointer', fontWeight: 600,
    fontSize: compact ? '0.75rem' : '0.8125rem',
    padding: compact ? '0.375rem 0.75rem' : '0.5rem 1rem',
    transition: 'background-color 0.15s, border-color 0.15s, transform 0.1s',
    border: '1px solid transparent', whiteSpace: 'nowrap',
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }} data-testid="linkedin-share-buttons">
      <button
        data-testid="linkedin-add-to-profile-btn"
        onClick={() => open(addToProfileUrl)}
        style={{ ...base, background: '#0A66C2', color: '#fff' }}
        onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
        onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
      >
        <Linkedin size={compact ? 14 : 16} strokeWidth={2.25} />
        Add to LinkedIn
      </button>
      <button
        data-testid="linkedin-share-post-btn"
        onClick={() => open(sharePostUrl)}
        style={{ ...base, background: '#fff', color: '#0A66C2', borderColor: '#0A66C2' }}
      >
        <Linkedin size={compact ? 14 : 16} strokeWidth={2.25} />
        Share
      </button>
      <button
        data-testid="copy-credential-link-btn"
        onClick={copyLink}
        style={{ ...base, background: '#F1F5F9', color: '#475569', borderColor: '#ECE7DE' }}
      >
        Copy link
      </button>
    </div>
  );
};

export default LinkedInShareButtons;
